import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';

const Privacy = () => {
  const navigate = useNavigate();

  return (
    <div className="page-content" style={{ maxWidth: '900px', margin: '0 auto', padding: '40px 20px' }}>
      <button 
        onClick={() => navigate(-1)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          background: 'none',
          border: 'none',
          color: '#9D4EDD',
          fontSize: '14px',
          fontWeight: '600',
          cursor: 'pointer',
          marginBottom: '32px',
          padding: '8px 0'
        }}
      >
        <ArrowLeft size={20} />
        Back
      </button>

      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        marginBottom: '32px'
      }}>
        <div style={{
          width: '60px',
          height: '60px',
          borderRadius: '12px',
          background: 'linear-gradient(135deg, #9D4EDD 0%, #C77DFF 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Shield size={32} color="white" />
        </div>
        <div>
          <h1 style={{ fontSize: '36px', fontWeight: 'bold', marginBottom: '8px' }}>
            Privacy Policy
          </h1>
          <p style={{ color: '#999', fontSize: '14px' }}>
            MetaSoul® Music
          </p>
        </div>
      </div>

      <div style={{
        background: 'rgba(157, 78, 221, 0.1)',
        border: '2px solid rgba(157, 78, 221, 0.3)',
        borderRadius: '16px',
        padding: '40px',
        lineHeight: '1.8',
        color: '#ccc'
      }}>
        <h2 style={{ fontSize: '24px', color: '#fff', marginBottom: '24px', marginTop: '0' }}>
          1. Introduction
        </h2>
        <p style={{ marginBottom: '20px' }}>
          Mystic Temple, Inc. ("Mystic Temple," "we," "us," or "our") operates the MetaSoul® Music streaming platform (the "Service"). We are committed to protecting your privacy. This Privacy Policy explains how we collect, use, share and protect personal information when you use our website, mobile applications or other services that link to this policy. By using the Service, you consent to the practices described herein.
        </p>
        <p style={{ marginBottom: '20px' }}>
          This policy applies only to our Service. It does not govern third‑party websites or services that may link to or be accessible from our Service. If you access our Service via a third‑party site or donate through a third‑party payment processor, that third party's privacy policy will also apply.
        </p>

        <h2 style={{ fontSize: '24px', color: '#fff', marginBottom: '24px', marginTop: '40px' }}>
          2. Information We Collect
        </h2>
        <p style={{ marginBottom: '20px' }}>
          We collect two broad categories of information: (i) "Personal Information," which can identify or contact an individual (such as a name, email address or phone number), and (ii) "Other Information," which does not directly identify an individual (such as device identifiers or aggregated usage data).
        </p>

        <h3 style={{ fontSize: '20px', color: '#fff', marginBottom: '16px', marginTop: '32px' }}>
          2.1 Information You Provide to Us
        </h3>
        <p style={{ marginBottom: '16px' }}>
          We collect Personal Information you voluntarily provide, for example when you:
        </p>
        <ul style={{ marginBottom: '20px', paddingLeft: '24px' }}>
          <li style={{ marginBottom: '12px' }}>
            <strong style={{ color: '#fff' }}>Register an account:</strong> You may be asked to provide your name, email address and to create a username and password. Additional optional information (such as your date of birth or content preferences) may also be collected.
          </li>
          <li style={{ marginBottom: '12px' }}>
            <strong style={{ color: '#fff' }}>Contact us or request support:</strong> If you send us an email, make a comment or otherwise communicate with us, we collect the information you provide so we can respond.
          </li>
          <li style={{ marginBottom: '12px' }}>
            <strong style={{ color: '#fff' }}>Participate in interactive features:</strong> If the Service offers comment sections, playlists or messaging, we may collect any User Content you submit (see our Terms & Conditions for details on User Content rights).
          </li>
          <li style={{ marginBottom: '12px' }}>
            <strong style={{ color: '#fff' }}>Make donations or purchases:</strong> If we offer donation options, we may collect billing and payment information. Payments are processed by third‑party payment processors; we do not store full credit‑card numbers.
          </li>
        </ul>

        <h3 style={{ fontSize: '20px', color: '#fff', marginBottom: '16px', marginTop: '32px' }}>
          2.2 Information We Collect Automatically
        </h3>
        <p style={{ marginBottom: '16px' }}>
          When you use the Service, we and our service providers collect certain information automatically, including:
        </p>
        <ul style={{ marginBottom: '20px', paddingLeft: '24px' }}>
          <li style={{ marginBottom: '12px' }}>
            <strong style={{ color: '#fff' }}>Device and usage information:</strong> The Internet Protocol (IP) address, browser type, operating system, device type and other technical information; the pages you visit before, during and after using the Service; the dates, times and duration of your interactions with our site; and your approximate geographic location derived from your IP address.
          </li>
          <li style={{ marginBottom: '12px' }}>
            <strong style={{ color: '#fff' }}>Cookies and tracking technologies:</strong> We use cookies, web beacons and similar technologies ("Tracking Technologies") to store user preferences, maintain sessions, understand usage and improve our offerings. These technologies help us recognize you across sessions, personalize your experience and gather analytics. By using the Service, you consent to our use of Tracking Technologies.
          </li>
          <li style={{ marginBottom: '12px' }}>
            <strong style={{ color: '#fff' }}>Analytics:</strong> We may use analytics tools to measure traffic and usage trends. These tools collect information such as your device identifiers, pages you visit and other interactions. We use this information to improve the Service.
          </li>
        </ul>

        <h3 style={{ fontSize: '20px', color: '#fff', marginBottom: '16px', marginTop: '32px' }}>
          2.3 Information from Third Parties
        </h3>
        <p style={{ marginBottom: '20px' }}>
          We may receive information about you from third parties, for example if you register or sign in via a social media account or if a partner provides information about your interactions with them. We may combine this information with information you provide us.
        </p>

        <h2 style={{ fontSize: '24px', color: '#fff', marginBottom: '24px', marginTop: '40px' }}>
          3. How We Use Your Information
        </h2>
        <p style={{ marginBottom: '16px' }}>
          We use the information we collect for purposes that include:
        </p>
        <ol style={{ marginBottom: '20px', paddingLeft: '24px' }}>
          <li style={{ marginBottom: '12px' }}>
            <strong style={{ color: '#fff' }}>Providing and improving the Service:</strong> To operate, maintain and provide the features and functionality of the Service; personalize content and recommendations; and understand how users interact with our Service so we can improve it.
          </li>
          <li style={{ marginBottom: '12px' }}>
            <strong style={{ color: '#fff' }}>Communications:</strong> To contact you about your account, respond to your inquiries, send administrative messages and provide information about updates or changes to the Service. We may also send you newsletters or promotional communications if you opt in.
          </li>
          <li style={{ marginBottom: '12px' }}>
            <strong style={{ color: '#fff' }}>Security and fraud prevention:</strong> To detect, prevent and address technical issues, unauthorized activities or fraudulent use and to protect the safety and integrity of our Service and users.
          </li>
          <li style={{ marginBottom: '12px' }}>
            <strong style={{ color: '#fff' }}>Legal compliance:</strong> To comply with applicable laws, regulations, court orders or other legal processes, and to protect our legal rights.
          </li>
          <li style={{ marginBottom: '12px' }}>
            <strong style={{ color: '#fff' }}>Aggregated and de‑identified data:</strong> We may aggregate or de‑identify information so that it does not identify you and use or share such information for research, analytics and marketing purposes.
          </li>
        </ol>
        <p style={{ marginBottom: '20px' }}>
          We will not sell your Personal Information. We do not engage in targeted advertising based on your listening habits. Our use of cookies and analytics is solely to improve the Service and personalize your experience.
        </p>

        <h2 style={{ fontSize: '24px', color: '#fff', marginBottom: '24px', marginTop: '40px' }}>
          4. How We Share Information
        </h2>
        <p style={{ marginBottom: '16px' }}>
          We may share your information in the following circumstances:
        </p>
        <ol style={{ marginBottom: '20px', paddingLeft: '24px' }}>
          <li style={{ marginBottom: '12px' }}>
            <strong style={{ color: '#fff' }}>Service providers:</strong> With vendors and service providers who help us operate the Service and perform functions such as hosting, data storage, analytics, customer support, communications and payment processing. These providers are contractually required to handle Personal Information only for the purposes of providing services to us and to protect your data.
          </li>
          <li style={{ marginBottom: '12px' }}>
            <strong style={{ color: '#fff' }}>Legal and safety reasons:</strong> To comply with legal obligations, respond to lawful requests, protect our rights or property, enforce our Terms, and protect the safety of users or the public.
          </li>
          <li style={{ marginBottom: '12px' }}>
            <strong style={{ color: '#fff' }}>Business transfers:</strong> In the event of a merger, reorganization or other corporate transaction involving Mystic Temple, your information may be transferred as part of that transaction. We will require any successor entity to honor this Privacy Policy.
          </li>
          <li style={{ marginBottom: '12px' }}>
            <strong style={{ color: '#fff' }}>With your consent:</strong> We may share your information for any other purpose disclosed at the time of collection or with your consent.
          </li>
        </ol>
        <p style={{ marginBottom: '20px' }}>
          We may share aggregated, non‑identifiable information publicly and with third parties for analytics, research or fundraising purposes. Aggregated data does not identify individual users.
        </p>

        <h2 style={{ fontSize: '24px', color: '#fff', marginBottom: '24px', marginTop: '40px' }}>
          5. Cookies and Similar Technologies
        </h2>
        <p style={{ marginBottom: '20px' }}>
          Cookies are small data files stored on your device. They help us understand user behavior, remember preferences and improve the Service. We use cookies to:
        </p>
        <ul style={{ marginBottom: '20px', paddingLeft: '24px' }}>
          <li style={{ marginBottom: '8px' }}>Recognize repeat visits and enable you to move between pages without logging in each time.</li>
          <li style={{ marginBottom: '8px' }}>Understand which features, pages or content are popular.</li>
          <li style={{ marginBottom: '8px' }}>Save your preferences and settings, such as language choice.</li>
        </ul>
        <p style={{ marginBottom: '20px' }}>
          Most browsers allow you to control cookies through their settings. However, disabling cookies may affect the functionality of the Service. Our website may also use third‑party analytics or embedded content that set their own cookies; these third parties are responsible for their cookie practices.
        </p>

        <h2 style={{ fontSize: '24px', color: '#fff', marginBottom: '24px', marginTop: '40px' }}>
          6. Data Retention
        </h2>
        <p style={{ marginBottom: '20px' }}>
          We retain Personal Information for as long as necessary to fulfil the purposes described in this policy, comply with legal obligations, resolve disputes and enforce our agreements. The retention period depends on the nature of the information and the purpose for which it was collected. We may retain cached or backup copies of your information for a reasonable period for disaster recovery.
        </p>

        <h2 style={{ fontSize: '24px', color: '#fff', marginBottom: '24px', marginTop: '40px' }}>
          7. Data Security
        </h2>
        <p style={{ marginBottom: '20px' }}>
          We use reasonable administrative, technical and physical safeguards designed to protect Personal Information from loss, misuse and unauthorized access or disclosure. However, no method of transmission over the internet or electronic storage is completely secure. We cannot guarantee absolute security; you use the Service at your own risk.
        </p>

        <h2 style={{ fontSize: '24px', color: '#fff', marginBottom: '24px', marginTop: '40px' }}>
          8. Your Rights and Choices
        </h2>
        <p style={{ marginBottom: '16px' }}>
          Depending on where you reside, you may have certain rights regarding your personal data. We extend these rights to all users in the United States, consistent with emerging privacy laws. These rights may include the ability to:
        </p>
        <ul style={{ marginBottom: '20px', paddingLeft: '24px' }}>
          <li style={{ marginBottom: '12px' }}>
            <strong style={{ color: '#fff' }}>Access and know:</strong> Request information about what data we hold about you and how we use it.
          </li>
          <li style={{ marginBottom: '12px' }}>
            <strong style={{ color: '#fff' }}>Correct:</strong> Request correction of inaccurate Personal Information.
          </li>
          <li style={{ marginBottom: '12px' }}>
            <strong style={{ color: '#fff' }}>Delete:</strong> Request deletion of certain Personal Information, subject to exceptions when we are required or permitted to retain it (for example, to comply with legal obligations or to defend against legal claims).
          </li>
          <li style={{ marginBottom: '12px' }}>
            <strong style={{ color: '#fff' }}>Opt out of marketing communications:</strong> You can unsubscribe from promotional emails by following the instructions in those messages. You cannot opt out of essential communications related to your account.
          </li>
          <li style={{ marginBottom: '12px' }}>
            <strong style={{ color: '#fff' }}>Withdraw consent:</strong> Where we rely on consent to process your data, you may withdraw your consent.
          </li>
        </ul>
        <p style={{ marginBottom: '20px' }}>
          To exercise any of these rights, please contact us at <strong style={{ color: '#fff' }}>privacy@MetaSoulMusic.com</strong>. We may need to verify your identity before processing your request. We will not discriminate against you for exercising your privacy rights.
        </p>

        <h2 style={{ fontSize: '24px', color: '#fff', marginBottom: '24px', marginTop: '40px' }}>
          9. Children's Privacy
        </h2>
        <p style={{ marginBottom: '20px' }}>
          The Service is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If you are under 13, please do not provide any personal information to us. If we learn that we have collected information from a child under 13 without parental consent, we will delete it. Parents or guardians who believe their child has provided us with personal information may contact us at <strong style={{ color: '#fff' }}>privacy@MetaSoulMusic.com</strong> to request deletion.
        </p>

        <h2 style={{ fontSize: '24px', color: '#fff', marginBottom: '24px', marginTop: '40px' }}>
          10. International Data Transfers
        </h2>
        <p style={{ marginBottom: '20px' }}>
          MetaSoul® Music operates from the United States. If you access the Service from outside the U.S., your information may be transferred to, stored and processed in the United States or other countries where our service providers operate. These countries may have different data protection laws than your country of residence. We take steps to ensure that your data receives an adequate level of protection in the jurisdictions where we process it.
        </p>

        <h2 style={{ fontSize: '24px', color: '#fff', marginBottom: '24px', marginTop: '40px' }}>
          11. Changes to This Privacy Policy
        </h2>
        <p style={{ marginBottom: '20px' }}>
          We may update this Privacy Policy from time to time. If we make material changes in how we collect or use your information, we will notify you by posting the updated policy on our website or by sending you an email. Your continued use of the Service after such changes constitutes acceptance of the revised policy. We encourage you to review this policy periodically.
        </p>

        <h2 style={{ fontSize: '24px', color: '#fff', marginBottom: '24px', marginTop: '40px' }}>
          12. Contact Us
        </h2>
        <p style={{ marginBottom: '0' }}>
          If you have any questions or concerns about this Privacy Policy or our data‑handling practices, please contact us at <strong style={{ color: '#fff' }}>privacy@MetaSoulMusic.com</strong>.
        </p>
      </div>
    </div>
  );
};

export default Privacy;
